import React from 'react';
import FieldReportHeader from './FieldReportHeader';
import FieldReportFooter from './FieldReportFooter';
import { MapPin, Users, CheckCircle, Clock, XCircle } from 'lucide-react';
import { resolveFileUrl, resolveSignatureUrl } from '../../lib/fileUtils';
import { SafeImage } from '../SafeImage';

// Helper to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-green-100 text-green-700';
    case 'submitted_pending_review': return 'bg-blue-100 text-blue-700';
    case 'draft': return 'bg-slate-100 text-slate-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'completed': return 'Completed';
    case 'submitted_pending_review': return 'Pending Review';
    case 'draft': return 'Draft';
    default: return status || 'Draft';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr).trim());
    const date = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true 
    });
  } catch {
    return dateStr;
  }
};

export default function PreConDocument({ report, printRef }) {
  return (
    <div 
      ref={printRef} 
      id="precon-document" 
      className="bg-white w-full max-w-4xl shadow-xl rounded-lg overflow-hidden print:shadow-none print:rounded-none print:max-w-none" 
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <FieldReportHeader reportType="PreCon Report" reportNumber={report.job_number || report.id?.slice(0, 8)} />
      
      <div className="px-6 py-4">
        {/* Title & Status */}
        <div className="mb-4 text-center">
          <h2 className="text-lg font-semibold text-slate-900">Pre-Construction Site Scouting Report</h2>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium ${getStatusColor(report.status)}`}>
            {getStatusLabel(report.status)}
          </span>
        </div>

        {/* Job & Site Info */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Job Information</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-[10px]">Job Number</p>
              <p className="font-medium text-amber-600">{report.job_number || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Visit Date</p>
              <p className="font-medium text-slate-700">{formatDate(report.visit_date)}</p>
            </div>
            <div>
              <p className="text-slate-500 text-[10px]">Lead On Site</p>
              <p className="font-medium text-slate-700">{report.cll_lead_on_site || '—'}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <p className="text-slate-500 text-[10px]">Site Location</p>
              <p className="font-medium text-slate-700 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-slate-400" />
                {report.site_location || '—'}
              </p>
            </div>
            {report.crew_members && (
              <div>
                <p className="text-slate-500 text-[10px]">Crew Members</p>
                <p className="font-medium text-slate-700 flex items-center gap-1">
                  <Users className="w-3 h-3 text-slate-400" />
                  {report.crew_members}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Project Details */}
        <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-100">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-600 mb-2">Project Details</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-blue-500 text-[10px]">Project Name</p>
              <p className="font-medium text-slate-700">{report.project_name || '—'}</p>
            </div>
            <div>
              <p className="text-blue-500 text-[10px]">Client/Owner</p>
              <p className="font-medium text-slate-700">{report.client_owner || '—'}</p>
            </div>
            <div>
              <p className="text-blue-500 text-[10px]">Project Number</p>
              <p className="font-medium text-slate-700">{report.project_number || '—'}</p>
            </div>
          </div>
          {report.scope_overview && (
            <div className="mt-3">
              <p className="text-blue-500 text-[10px]">Scope Overview</p>
              <p className="text-sm text-slate-700">{report.scope_overview}</p>
            </div>
          )}
        </div>

        {/* Site Assessment Notes */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Site Assessment</p>
          <div className="space-y-3">
            {report.access_notes && (
              <div className="p-2 bg-slate-50 rounded">
                <p className="text-slate-500 text-[10px] font-semibold">Access Notes</p>
                <p className="text-sm text-slate-700">{report.access_notes}</p>
              </div>
            )}
            {report.safety_notes && (
              <div className="p-2 bg-red-50 rounded border border-red-100">
                <p className="text-red-500 text-[10px] font-semibold">Safety Concerns</p>
                <p className="text-sm text-slate-700">{report.safety_notes}</p>
              </div>
            )}
            {report.utility_awareness_notes && (
              <div className="p-2 bg-amber-50 rounded border border-amber-100">
                <p className="text-amber-600 text-[10px] font-semibold">Utility Awareness</p>
                <p className="text-sm text-slate-700">{report.utility_awareness_notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Photos Section */}
        {report.scouting_photos && report.scouting_photos.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Site Photos ({report.scouting_photos.length})</p>
            <div className="grid grid-cols-2 gap-4">
              {report.scouting_photos.map((photo, idx) => (
                <div key={idx} className="break-inside-avoid">
                  <SafeImage src={resolveFileUrl(photo, 'preview')} alt={`Site Photo ${idx + 1}`} className="w-full h-80 object-cover rounded border" loading="lazy" decoding="async" />
                  {photo.caption && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">{photo.caption}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Notes */}
        {report.additional_notes && (
          <div className="mb-4 p-3 bg-slate-50 rounded">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Additional Notes</p>
            <p className="text-sm text-slate-600">{report.additional_notes}</p>
          </div>
        )}

        {/* Signatures Section */}
        <div className="mb-4 border-t border-slate-200 pt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Signatures & Authorization</p>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Submitter Signature */}
            <div className="p-3 bg-slate-50 rounded">
              <p className="text-[10px] font-semibold text-slate-500 mb-2">Submitter / Prepared By</p>
              {report.submitter_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(report, 'submitter_signature')} 
                    alt="Submitter Signature" 
                    className="max-h-24 mb-2 border border-slate-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-slate-700">{report.submitter_name || report.submitted_by || '—'}</p>
                  <p className="text-[10px] text-slate-500">{formatDateTime(report.submitter_signed_at)}</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-300 text-sm italic pb-1">Not signed</span>
                  </div>
                  <p className="text-[10px] text-slate-500">Technician Signature & Date</p>
                </div>
              )}
            </div>

            {/* Reviewer/Approver Signature */}
            <div className={`p-3 rounded ${report.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-slate-50'}`}>
              <p className={`text-[10px] font-semibold mb-2 ${report.status === 'completed' ? 'text-green-600' : 'text-slate-500'}`}>
                Manager Approval
              </p>
              {report.reviewer_signature_url ? (
                <div>
                  <img 
                    src={resolveSignatureUrl(report, 'reviewer_signature')} 
                    alt="Reviewer Signature" 
                    className="max-h-24 mb-2 border border-green-200 rounded bg-white p-1" 
                  />
                  <p className="text-sm font-medium text-green-700">{report.reviewer_name || report.reviewed_by || '—'}</p>
                  <p className="text-[10px] text-green-600">{formatDateTime(report.reviewer_signed_at || report.reviewed_at)}</p>
                </div>
              ) : (
                <div>
                  <div className="border-b-2 border-slate-300 h-12 mb-1 flex items-end">
                    <span className="text-slate-300 text-sm italic pb-1">
                      {report.status === 'submitted_pending_review' ? 'Awaiting approval' : 'Not signed'}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">Manager Signature & Date</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status History */}
        <div className="mb-4 p-3 bg-slate-50 rounded">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Report Status</p>
          <div className="flex flex-wrap gap-4 text-[11px]">
            {report.created_at && (
              <div className="flex items-center gap-1 text-slate-600">
                <Clock className="w-3 h-3" />
                <span>Created: {formatDateTime(report.created_at)}</span>
              </div>
            )}
            {report.submitted_at && (
              <div className="flex items-center gap-1 text-blue-600">
                <CheckCircle className="w-3 h-3" />
                <span>Submitted: {formatDateTime(report.submitted_at)}</span>
              </div>
            )}
            {report.reviewed_at && report.status === 'completed' && (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-3 h-3" />
                <span>Approved: {formatDateTime(report.reviewed_at)}</span>
              </div>
            )}
            {report.rejected_at && (
              <div className="flex items-center gap-1 text-red-600">
                <XCircle className="w-3 h-3" />
                <span>Rejected: {formatDateTime(report.rejected_at)}</span>
              </div>
            )}
          </div>
          {report.rejection_reason && (
            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
              <span className="font-semibold">Rejection Reason:</span> {report.rejection_reason}
            </div>
          )}
        </div>
      </div>
      
      {/* Legal Disclaimer */}
      <div className="px-6 py-3 bg-slate-100 border-t border-slate-200">
        <p className="text-[9px] text-slate-500 text-center italic">
          This report documents field observations at the time of service. 
          Utility locations are approximate unless physically exposed.
        </p>
      </div>
      
      {/* Footer bar */}
      <div className="px-6 py-3 bg-slate-800 flex items-center justify-between text-[10px] text-slate-300">
        <span>Center Line Locating, LLC · Charlotte, NC · (704) 315-5820</span>
        <span>PreCon Report #{report.job_number || report.id?.slice(0, 8)}</span>
      </div>
    </div>
  );
}
